import { useMemo, useState } from 'react'
import { Alert, DatePicker, Drawer, Segmented, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { defaultDayRange, rangeToIso } from '@/shared/lib/dayRange'
import { formatDateTime, formatNumber, utcTooltip } from '@/shared/lib/format'
import { DataTable } from '@/shared/ui/table/DataTable'
import {
  JOB_OUTCOMES,
  fetchJobRunPage,
  jobMessageText,
  type Job,
  type JobOutcome,
  type JobRun,
} from './jobsApi'

const { RangePicker } = DatePicker

/**
 * ⚠️ `NOTHING` KULRANG, QIZIL EMAS. Bu butun sahifadagi eng muhim rang
 * qarori: "yurdi, lekin ishi yo'q edi" ODATIY holat va uni xato rangida
 * ko'rsatish operatorni bir haftada ranglarga umuman qaramaydigan qilib
 * qo'yardi. `jobsApi.needsAttention` dagi bilan bir xil mulohaza.
 */
const OUTCOME_COLOR: Record<JobOutcome, string> = {
  OK: 'green',
  NOTHING: 'default',
  PARTIAL: 'gold',
  FAILED: 'red',
}

/**
 * Standart oraliq — oxirgi 7 kun (`AuditPage` bilan bir xil qaror).
 *
 * ⚠️ TENANT zonasida: `dayjs()` brauzernikini olardi (`dayRange.ts`).
 */
const defaultRange = (): [Dayjs, Dayjs] => defaultDayRange(7)

interface JobRunsDrawerProps {
  /** `null` — oyna yopiq */
  job: Job | null
  onClose: () => void
}

/**
 * Bitta vazifaning yurish tarixi.
 *
 * <h2>⚠️ NEGA OYNA (Drawer), ALOHIDA SAHIFA EMAS</h2>
 *
 * Alohida `/jobs/runs` sahifasi tarixni ko'rsatish uchun yetarli
 * bo'lardi, lekin operator u yerga KONTEKSTSIZ tushardi: u endigina
 * bosgan qator qaysi servis va qaysi klass ekanini tanlagichlardan
 * QAYTA tanlashi kerak bo'lardi. Bu sahifaning butun ma'nosiga zid —
 * u qidirmasdan ko'rish uchun qurilgan. Oyna esa reestrni ekranda
 * qoldiradi va yopilgach operator o'sha qatorga qaytadi.
 *
 * ⚠️ `expandable` ham bo'lardi, lekin tarix SAHIFALANADI (`/api/jobs/runs`
 * — `{rows, page, size, hasNext}`). Sahifalanadigan jadvalni yig'ilib
 * ochiladigan qatorga tiqish sahifalash tugmalarini jadval ichida
 * ikkinchi marta paydo qilardi va URL holati (`useTableUrlState`) qaysi
 * qatorga tegishli ekani noaniq bo'lardi.
 *
 * <h2>⚠️ SANA ORALIG'I MAJBURIY</h2>
 *
 * Backend `from`/`to` siz so'rovni 400 bilan rad etadi (`JobRunQuery`
 * kompakt konstruktori). Shuning uchun `allowClear={false}` — bo'sh
 * holatga tushirishning iloji yo'q. Bu `AuditPage` dagi bilan bir xil
 * qaror va bir xil sabab: tarix jadvali o'sadi.
 *
 * <h2>⚠️ `OK` FILTRI — 2026-08-13 SAVOLINING JAVOBI</h2>
 *
 * "Bu vazifa oxirgi marta qachon ROSTDAN ish bajardi?" degan savolga
 * `lastOkAt` JAVOB BERMAYDI: `NOTHING` ham o'sha ustunni yangilaydi
 * (`jobsApi.Job.lastOkAt` izohi). Faqat `outcome=OK` bo'yicha
 * filtrlangan tarix javob beradi — shuning uchun bu filtr shu yerda va
 * ko'rinadigan joyda turadi.
 */
export function JobRunsDrawer({ job, onClose }: JobRunsDrawerProps) {
  const { t } = useTranslation()
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange)
  const [outcome, setOutcome] = useState<JobOutcome | null>(null)

  const { from, to } = rangeToIso(range)

  const service = job?.service ?? null
  const name = job?.job ?? null

  const fetchPage = useMemo(
    () => fetchJobRunPage({ service, job: name, outcome, from, to }),
    [service, name, outcome, from, to],
  )

  const columns = useMemo<ColumnsType<JobRun>>(
    () => [
      {
        key: 'startedAt',
        title: t('jobs.history.columns.startedAt'),
        width: 170,
        render: (_, row) => (
          <Typography.Text title={utcTooltip(row.startedAt)}>
            {formatDateTime(row.startedAt)}
          </Typography.Text>
        ),
      },
      {
        key: 'outcome',
        title: t('jobs.history.columns.outcome'),
        width: 150,
        render: (_, row) => (
          <Tooltip title={t(`jobs.outcomeHint.${row.outcome}`)}>
            <Tag color={OUTCOME_COLOR[row.outcome]}>{t(`jobs.outcome.${row.outcome}`)}</Tag>
          </Tooltip>
        ),
      },
      {
        key: 'items',
        title: t('jobs.history.columns.items'),
        width: 110,
        align: 'right',
        render: (_, row) => formatNumber(row.items),
      },
      {
        key: 'durationMs',
        title: t('jobs.history.columns.duration'),
        width: 120,
        align: 'right',
        render: (_, row) => t('jobs.msValue', { value: formatNumber(row.durationMs) }),
      },
      {
        key: 'message',
        title: t('jobs.history.columns.message'),
        // ⚠️ Xato XABARDAN USTUN chiqadi: yiqilgan yurishda `message`
        // ham bo'lishi mumkin, lekin operatorga kerakligi — sabab.
        render: (_, row) =>
          row.error ? (
            <Typography.Text type="danger">{row.error}</Typography.Text>
          ) : (
            (jobMessageText(row.messageCode, row.messageParams, row.message, t)
              ?? t('common.dash'))
          ),
      },
    ],
    [t],
  )

  return (
    <Drawer
      open={job !== null}
      onClose={onClose}
      width={920}
      destroyOnHidden
      title={job ? `${job.job} · ${job.service}` : ''}
    >
      {job === null ? null : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type="info" showIcon message={t('jobs.history.note')} />

          <Space wrap>
            <RangePicker
              value={range}
              onChange={(value) => {
                // `allowClear={false}` bilan `value` amalda hech qachon
                // `null` bo'lmaydi, lekin antd tipi baribir shunday
                // ruxsat beradi — himoya shart (`AuditPage` andozasi).
                if (value?.[0] && value[1]) setRange([value[0], value[1]])
              }}
              allowClear={false}
              format="DD.MM.YYYY"
            />
            <Segmented<string>
              value={outcome ?? 'ALL'}
              onChange={(value) => setOutcome(value === 'ALL' ? null : (value as JobOutcome))}
              options={[
                { value: 'ALL', label: t('jobs.history.allOutcomes') },
                ...JOB_OUTCOMES.map((value) => ({ value, label: t(`jobs.outcome.${value}`) })),
              ]}
            />
          </Space>

          <DataTable<JobRun>
            queryKey={queryKeys.jobs.history(service, name, outcome, from, to)}
            fetchPage={fetchPage}
            columns={columns}
            rowKey={(row) => row.id}
            tableId="job-runs"
            urlPrefix="runs"
            showTotal={false}
            scrollX={900}
            emptyTitle={t('jobs.history.empty')}
            emptyDescription={t('jobs.history.emptyHint')}
            expandable={{
              rowExpandable: (row) => Boolean(row.detail) || Boolean(row.error),
              expandedRowRender: (row) => (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {row.error ? (
                    <Typography.Paragraph
                      type="danger"
                      code
                      style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                    >
                      {row.error}
                    </Typography.Paragraph>
                  ) : null}
                  <Typography.Paragraph code style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                    {row.detail ?? t('jobs.history.noDetail')}
                  </Typography.Paragraph>
                </Space>
              ),
            }}
          />
        </Space>
      )}
    </Drawer>
  )
}
